/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { Trans } from "@lingui/macro";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@stylingcv/ui";

// Who are you, and why did you build Stylion?

// Is Stylion really free to use?
const Question1 = () => (
  <AccordionItem value="1">
    <AccordionTrigger className="text-left leading-relaxed">
      <Trans>Is Stylion really free to use?</Trans>
    </AccordionTrigger>
    <AccordionContent className="prose max-w-none dark:prose-invert">
      <p>
        <strong>Yes!</strong>{" "}
        <Trans>
          Stylion is completely free to start. You get free tokens when you sign up to try all
          our AI-powered features including resume generation, content suggestions, and ATS
          optimization.
        </Trans>
      </p>

      <p>
        <Trans>
          Our free tier includes access to all professional templates, basic editing features, and
          PDF export. You can create and download professional resumes without any cost.
        </Trans>
      </p>

      <p>
        <Trans>
          For heavy AI usage, we offer affordable token packages and subscription plans that unlock
          unlimited AI features, priority support, and advanced analytics. But you can definitely
          get started and create great resumes completely free!
        </Trans>
      </p>
    </AccordionContent>
  </AccordionItem>
);

// How accurate is the AI resume generation?
const Question2 = () => (
  <AccordionItem value="2">
    <AccordionTrigger className="text-left leading-relaxed">
      <Trans>How accurate is the AI resume generation?</Trans>
    </AccordionTrigger>
    <AccordionContent className="prose max-w-none dark:prose-invert">
      <p>
        <Trans>
          Our AI is trained on thousands of successful resumes and job descriptions across various
          industries. It understands what makes resumes effective and tailors content specifically
          to your target role.
        </Trans>
      </p>

      <p>
        <strong>
          <Trans>
            <Trans>The AI analyzes your job requirements</Trans>
          </Trans>
        </strong>{" "}
        and generates relevant bullet points, optimizes keywords for ATS systems, and ensures proper
        formatting. You maintain full control to edit and customize any generated content.
      </p>

      <p>
        <Trans>
          We continuously improve our AI models based on user feedback and industry best practices.
          Most users report that AI-generated content requires minimal editing and significantly
          speeds up their resume creation process.
        </Trans>
      </p>
    </AccordionContent>
  </AccordionItem>
);

// Do you support Arabic resumes?
const Question3 = () => (
  <AccordionItem value="3">
    <AccordionTrigger className="text-left leading-relaxed">
      <Trans>Do you support Arabic resumes?</Trans>
    </AccordionTrigger>
    <AccordionContent className="prose max-w-none dark:prose-invert">
      <p>
        <strong>
          <Trans>
            <Trans>Absolutely!</Trans>
          </Trans>
        </strong>{" "}
        Stylion has full RTL (Right-to-Left) support for Arabic resumes. Toggle RTL in the
        header, and the editor and templates mirror to right-to-left layout perfectly.
      </p>

      <p>
        <Trans>
          Our AI understands Arabic job markets and can generate culturally appropriate content for
          Arabic-speaking regions. All templates work seamlessly in both Arabic and English,
          maintaining professional formatting and proper text alignment.
        </Trans>
      </p>

      <p>
        <Trans>
          You can create bilingual resumes, switch between languages easily, and export
          pixel-perfect PDFs that look great in both Arabic and English. Perfect for professionals
          working in Middle Eastern markets or international companies.
        </Trans>
      </p>
    </AccordionContent>
  </AccordionItem>
);

// Are the resumes ATS-compatible?
const Question4 = () => (
  <AccordionItem value="4">
    <AccordionTrigger className="text-left leading-relaxed">
      <Trans>Are the resumes ATS-compatible?</Trans>
    </AccordionTrigger>
    <AccordionContent className="prose max-w-none dark:prose-invert">
      <p>
        <strong>Yes!</strong> All our templates use clean HTML/CSS with semantic sections, ensuring
        your exported PDFs parse reliably through Applicant Tracking Systems (ATS).
      </p>

      <p>
        Our AI specifically optimizes your resume content for ATS compatibility by suggesting
        relevant keywords, proper formatting, and industry-standard section structures. We achieve
        85-95% ATS compatibility scores across all templates.
      </p>

      <p>
        <Trans>
          The system automatically checks for common ATS issues like complex formatting, images in
          text areas, or problematic fonts, ensuring your resume gets past automated screening
          systems and reaches human recruiters.
        </Trans>
      </p>
    </AccordionContent>
  </AccordionItem>
);

// How does the AI Integration work?
const Question5 = () => (
  <AccordionItem value="5">
    <AccordionTrigger className="text-left leading-relaxed">
      <Trans>How does the AI Integration work?</Trans>
    </AccordionTrigger>
    <AccordionContent className="prose max-w-none dark:prose-invert">
      <p>
        <Trans>
          Our AI integration uses advanced language models to analyze job descriptions and generate
          tailored resume content. Simply paste a job URL or describe your target role, and the AI
          creates relevant bullet points, skills, and achievements.
        </Trans>
      </p>

      <p>
        <strong>
          <Trans>
            <Trans>No API keys required from you!</Trans>
          </Trans>
        </strong>{" "}
        We've built AI features directly into the platform using our managed integration. Simply use
        tokens from your account balance to access features like content generation, writing
        assistance, and ATS optimization.
      </p>

      <p>
        <Trans>
          Token costs are clearly displayed for each AI feature before use, so you know exactly what
          you're paying for. Features include smart content suggestions, tone adjustments, grammar
          improvements, and comprehensive resume analysis.
        </Trans>
      </p>
    </AccordionContent>
  </AccordionItem>
);

// Do you offer refunds?
const Question6 = () => (
  <AccordionItem value="6">
    <AccordionTrigger className="text-left leading-relaxed">
      <Trans>Do you offer refunds?</Trans>
    </AccordionTrigger>
    <AccordionContent className="prose max-w-none dark:prose-invert">
      <p>
        <Trans>
          At Stylion, we want you to be absolutely certain our platform meets your needs before
          you make a commitment. To ensure complete transparency, we provide you with a{" "}
        </Trans>
        <strong>
          <Trans>fully functional experience of our application completely free of charge.</Trans>
        </strong>
      </p>

      <p>
        <Trans>
          You can explore, test, and utilize all of our premium building and design features — the
          only feature reserved exclusively for our premium subscribers is the ability to{" "}
        </Trans>
        <strong>
          <Trans>download the final resume.</Trans>
        </strong>
      </p>

      <p>
        <Trans>
          Because this approach empowers you to thoroughly evaluate the entire application prior to
          purchase, we do not offer refunds on subscriptions. We encourage you to take full
          advantage of this free access to ensure Stylion is the perfect fit for your career
          journey before upgrading.
        </Trans>
      </p>
    </AccordionContent>
  </AccordionItem>
);

export const FAQSection = () => (
  <section id="faq" className="relative bg-[#050505] py-24 sm:py-32">
    <div className="container relative mx-auto px-4 sm:px-6">
      <div className="grid gap-12 lg:grid-cols-3">
        <div className="space-y-6">
          <div className="mb-4">
            <span className="rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] backdrop-blur-md">
              <Trans>FAQ</Trans>
            </span>
          </div>
          <h2 className="font-sans text-3xl font-bold tracking-tighter text-white sm:text-4xl md:text-5xl">
            <Trans>Frequently Asked Questions</Trans>
          </h2>

          <p className="text-lg font-light leading-relaxed text-white/60">
            <Trans>
              Everything you need to know about creating professional resumes with AI assistance.
            </Trans>
          </p>
        </div>

        <div className="col-span-2 rounded-[2rem] border border-white/10 bg-[#0a0a0a] p-6 sm:p-10 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 rounded-[2rem] shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] pointer-events-none" />
          <Accordion collapsible type="single" className="text-white/80">
            <Question1 />
            <Question2 />
            <Question3 />
            <Question4 />
            <Question5 />
            <Question6 />
          </Accordion>
        </div>
      </div>
    </div>
  </section>
);
